/** Fullscreen helpers — keep WebGL + input alive across enter/exit. */

import gsap from 'gsap';
import { resetHeldKeys } from './input';

export function getFullscreenElement(): Element | null {
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
  };
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

export function isFullscreen(): boolean {
  return !!getFullscreenElement();
}

/** Prefer the app shell so layout % heights still resolve inside fullscreen. */
export function getFullscreenTarget(): HTMLElement {
  return (
    document.querySelector<HTMLElement>('.app-shell') ??
    document.getElementById('root') ??
    document.documentElement
  );
}

export async function toggleFullscreen(ev?: Event): Promise<void> {
  ev?.preventDefault();
  ev?.stopPropagation();
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
  };
  const el = getFullscreenTarget() as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };

  try {
    if (isFullscreen()) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else doc.webkitExitFullscreen?.();
    } else if (el.requestFullscreen) {
      await el.requestFullscreen();
    } else {
      el.webkitRequestFullscreen?.();
    }
  } catch {
    // User gesture / policy rejection — ignore
  }

  // Blur the chrome button so Space/Enter don't re-toggle; keep window keyed.
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
  window.focus();
  repairAfterFullscreen();
}

/**
 * Fullscreen transitions can leave canvases at 0×0 for a frame, pause GSAP,
 * and drop keyup events. Force a wake + resize pulse.
 */
export function repairAfterFullscreen() {
  resetHeldKeys();

  try {
    gsap.ticker.wake?.();
    gsap.ticker.lagSmoothing(500, 33);
  } catch {
    /* gsap optional path */
  }

  const pulse = () => {
    window.dispatchEvent(new Event('resize'));
    document.querySelectorAll('canvas').forEach((node) => {
      const c = node as HTMLCanvasElement;
      c.style.width = '100%';
      c.style.height = '100%';
      void c.offsetWidth;
      void c.clientHeight;
    });
  };

  pulse();
  requestAnimationFrame(() => {
    pulse();
    requestAnimationFrame(pulse);
  });
  window.setTimeout(pulse, 80);
  window.setTimeout(pulse, 200);
}

/** Keep mouseup on this control so fullscreen layout jump doesn't click Skip / path CTAs. */
export function onFullscreenButtonPointerDown(
  e: { currentTarget: EventTarget; pointerId: number; preventDefault: () => void; stopPropagation: () => void },
) {
  e.stopPropagation();
  const el = e.currentTarget as HTMLElement;
  if (typeof el.setPointerCapture === 'function') {
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* not a pointer event target */
    }
  }
  void toggleFullscreen();
}

let listening = false;

/** Install once at app root. */
export function attachFullscreenRepair() {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  const onChange = () => repairAfterFullscreen();
  document.addEventListener('fullscreenchange', onChange);
  document.addEventListener('webkitfullscreenchange', onChange as EventListener);
}
